import i18next from 'i18next'

/**
 * Removes any HTML or MathML from the given string.
 *
 * @param {string} string
 * @returns {string}
 */
export function removeTags(string) {
  return string.replace(/<\/?\w[^>]*>/gi, '')
}

/**
 * Removes only the given tag from the given string.
 *
 * @param {string} string
 * @param {string} tag
 * @returns {string}
 */
export function removeTag(string, tag) {
  return string.replace(new RegExp(`</?${tag}[^>]*>`, 'g', 'i'), '')
}

/**
 * Converts a title, id and optional separator to a slug for use in links
 * @param {string} title Page title.
 * @param {string|number} id Page ID.
 * @param {string=} separator Optional separator - defaults to '-'.
 * @return {string} The slug.
 */
export function createSlug(title, id, separator = '-') {
  title = title.toLowerCase()
  title = title.replace(/à|á|â/g, 'a')
  title = title.replace(/å|Å/g, 'aa')
  title = title.replace(/è|é|ê|ë/g, 'e')
  title = title.replace(/ì|í|î/g, 'i')
  title = title.replace(/ò|ó|ô|ø|Ø/g, 'oe')
  title = title.replace(/ù|ú|û/g, 'u')
  title = title.replace(/ä|æ|Æ/g, 'ae')
  title = title.replace(/ö|Ö/g, 'oe')
  title = title.replace(/ü|Ü/g, 'ue')
  title = title.replace(/[^\w\s]/g, ' ')
  title = title.replace(/\s+/g, separator)

  return title + separator + id
}

/**
 * Extracts the ID from the given slug.
 *
 * @param {string} slug
 * @param {string=} separator Optional separator - defaults to '-'.
 * @return {Number} The slug.
 */
export function getIdFromSlug(slug, separator = '-') {
  return parseInt(slug.split(separator).pop())
}

/**
 * This function pads a string with a character (padStr) to a specific length (length)
 *
 * @param {string|number} str The string to be padded.
 * @param {number} length The final length of the string.
 * @param {string=} padStr The string to pad with (default to 0).
 * @return {string} The original string padded as requested.
 */
export function leftPad(str, length, padStr) {
  padStr = padStr || '0'

  if (typeof str === 'number') {
    str = str + ''
  }

  while (str.length < length) {
    str = padStr + str
  }

  return str
}

/**
 * Converts any escaped HTML characters to their original characters.
 * This might be vulnerable to XSS attacks and should never be used on user input.
 * Copied from https://css-tricks.com/snippets/javascript/unescape-html-in-js/
 *
 * @param {string} string
 * @return {string}
 */
export function unescapeHtml(string) {
  const e = document.createElement('div')

  e.innerHTML = string

  return e.childNodes.length === 0 ? '' : e.childNodes[0].nodeValue
}

/**
 * Converts camelCase to snake_case.
 *
 * @param string
 * @return {string}
 */
export function camelToSnake(string) {
  return string
    .replace(/[A-Z]|\d+/g, match => {
      return `_${match.toLowerCase()}`
    })
    .replace(/^_/, '')
}

/**
 * Converts camelCase to kebab-case.
 *
 * @param string
 * @return {string}
 */
export function camelToKebab(string) {
  return string
    .replace(/[A-Z]|\d+/g, match => {
      return `-${match.toLowerCase()}`
    })
    .replace(/^-/, '')
}

/**
 * Converts snake_case to camelCase.
 *
 * @param string
 * @return {string}
 */
export function snakeToCamel(string) {
  return string.replace(/_[\w\d]/g, match => {
    return match.substr(1).toUpperCase()
  })
}

/**
 * Converts an array to a comma separated string.
 *
 * @param {Array<string|number>} array An array of strings or numbers.
 * @param {(boolean|function(string):string|undefined)=} addQuotes Set boolean true to add quotes on either side of each string or pass a function that takes a string and returns a new string.
 * @param {boolean=} disableAnd Inserts ', ' instead of ' and ' between the last two strings.
 * @return {string}
 */
export function arrayToString(array, addQuotes, disableAnd) {
  if (!Array.isArray(array)) {
    return ''
  }

  let output = ''

  let stringParser

  if (addQuotes === true) {
    // If addQuotes is a boolean true
    stringParser = string => `"${string}"`
  } else if (addQuotes) {
    // If addQuotes is a function
    stringParser = addQuotes
  }

  array.forEach((entry, index) => {
    if (index > 0) {
      if (index >= array.length - 1 && !disableAnd) {
        output += ' ' + i18next.t('words.and') + ' '
      } else {
        output += ', '
      }
    }

    if (stringParser) {
      output += stringParser(entry)
    } else {
      output += entry
    }
  })

  return output
}
